import {
  Activity,
  CalendarDays,
  Check,
  ChevronRight,
  CircleOff,
  Clock3,
  LogOut,
  Pause,
  Play,
  Radio,
  RefreshCw,
  Shield,
  WifiOff,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { api, type DashboardData } from '../lib/api';
import { supabase } from '../lib/supabase';
import { agentDetails, agentHeartbeatActive } from '../lib/agent-status';
import { Logo } from './Logo';

const platforms = [
  { id: 'instagram', name: 'Instagram', monogram: 'IG' },
  { id: 'facebook', name: 'Facebook', monogram: 'f' },
  { id: 'tiktok', name: 'TikTok', monogram: '♪' },
] as const;

export function Dashboard({
  session,
  onOpenEditorial,
  onOpenMedia,
  onOpenSocial,
  onOpenIntelligence,
}: {
  session: Session;
  onOpenEditorial: () => void;
  onOpenMedia: () => void;
  onOpenSocial: () => void;
  onOpenIntelligence: () => void;
}) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setError(null);
      setData(await api.dashboard(session));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Erro inesperado');
    }
  }, [session]);
  useEffect(() => {
    void load();
  }, [load]);

  async function togglePause() {
    if (!data) return;
    setBusy(true);
    try {
      await api.setPaused(session, !data.automation.automation_paused);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Erro inesperado');
    } finally {
      setBusy(false);
    }
  }

  async function runCycle() {
    setBusy(true);
    try {
      await api.runCycle(session);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Erro inesperado');
    } finally {
      setBusy(false);
    }
  }

  if (!data && !error)
    return (
      <div className="center-state">
        <span className="spinner" />
        <p>Carregando seu Control Plane…</p>
      </div>
    );
  if (!data)
    return (
      <div className="center-state">
        <CircleOff />
        <h2>Não foi possível carregar</h2>
        <p>{error}</p>
        <button className="secondary" onClick={() => void load()}>
          <RefreshCw size={17} /> Tentar novamente
        </button>
      </div>
    );

  const paused = data.automation.automation_paused;
  const agentOnline = agentHeartbeatActive(
    data.localAgent?.status,
    data.localAgent?.last_seen_at,
  );
  const lastCycle = data.lastCycle
    ? new Date(data.lastCycle.started_at).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : 'Ainda não executado';
  return (
    <div className="app-shell">
      <header>
        <Logo />
        <div className="header-actions">
          <span
            className={paused ? 'status-pill paused' : 'status-pill active'}
          >
            <i /> {paused ? 'AUTOMAÇÕES PAUSADAS' : 'AUTOMAÇÕES ATIVAS'}
          </span>
          <button
            className="icon-button"
            aria-label="Sair"
            onClick={() => {
              void supabase?.auth.signOut();
            }}
          >
            <LogOut size={19} />
          </button>
        </div>
      </header>
      <main className="dashboard">
        <div className="welcome">
          <div>
            <p className="eyebrow">
              {new Date()
                .toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })
                .toUpperCase()}
            </p>
            <h1>Hoje, no controle.</h1>
            <p>Visão operacional do seu ecossistema de conteúdo.</p>
          </div>
          <div className="welcome-actions">
            <button className="secondary" onClick={onOpenEditorial}>
              Abrir cérebro editorial
            </button>
            <button className="secondary" onClick={onOpenMedia}>
              Abrir Fábrica de Conteúdo
            </button>
            <button className="secondary" onClick={onOpenSocial}>
              Conexões e Publicações
            </button>
            <button className="secondary" onClick={onOpenIntelligence}>
              Otimizador e inteligência
            </button>
            <button
              className="secondary"
              disabled={busy || paused}
              onClick={() => {
                void runCycle();
              }}
            >
              <Play size={17} /> Planejar hoje
            </button>
          </div>
        </div>
        {error && (
          <div className="error-banner" role="alert">
            {error}
            <button onClick={() => setError(null)}>Fechar</button>
          </div>
        )}
        <section className="metrics-grid">
          <article className="metric-card">
            <div className="metric-head">
              <span>APRENDIZADO</span>
              <Activity size={19} />
            </div>
            <strong>
              {data.intelligence?.observation_count
                ? data.intelligence.observation_count
                : '—'}
            </strong>
            <p>
              {data.intelligence?.observation_count
                ? 'publicações com métricas analisadas'
                : 'SEM DADOS SUFICIENTES'}
            </p>
          </article>
          <article className="metric-card lime">
            <div className="metric-head">
              <span>PROGRAMADOS HOJE</span>
              <CalendarDays size={19} />
            </div>
            <strong>{data.scheduledToday}</strong>
            <p>
              {data.scheduledToday === 0
                ? 'Nenhum conteúdo na fila'
                : 'Conteúdos aguardando horário'}
            </p>
          </article>
          <article className="metric-card">
            <div className="metric-head">
              <span>ÚLTIMO CICLO</span>
              <RefreshCw size={19} />
            </div>
            <strong className="metric-text">{lastCycle}</strong>
            <p>{data.lastCycle?.status ?? 'Sem execução registrada'}</p>
          </article>
          <article className="metric-card">
            <div className="metric-head">
              <span>PRÓXIMO CICLO</span>
              <Clock3 size={19} />
            </div>
            <strong className="metric-text">
              {data.automation.daily_cycle_time}
            </strong>
            <p>{data.automation.timezone.replace('_', ' ')}</p>
          </article>
        </section>
        <section className="panel automation" aria-label="Saúde do sistema">
          <div className="panel-title">
            <div>
              <p className="eyebrow">DIAGNÓSTICO SEM SEGREDOS</p>
              <h2>Saúde do sistema</h2>
            </div>
          </div>
          <p>API: respondendo · Supabase: leitura autenticada disponível</p>
          <p>
            R2:{' '}
            {data.health?.r2BindingConfigured
              ? 'binding configurado'
              : 'não verificado'}{' '}
            · Queue:{' '}
            {data.health?.queueBindingConfigured
              ? 'binding configurado'
              : 'não verificado'}
          </p>
          <p>
            Mídia assinada:{' '}
            {data.health?.mediaSigningConfigured
              ? 'configurada'
              : 'configuração não confirmada'}{' '}
            · Agente:{' '}
            {agentOnline ? 'heartbeat recente' : 'offline ou não provisionado'}
          </p>
          <p className="panel-note">
            Bindings configurados não comprovam leitura do R2 nem processamento
            da fila. Cron e provedores sociais exigem validação separada.
          </p>
        </section>
        <section className="main-grid">
          <article className="panel platforms">
            <div className="panel-title">
              <div>
                <p className="eyebrow">CANAIS</p>
                <h2>Plataformas</h2>
              </div>
              <span>
                {
                  new Set(
                    data.accounts
                      .filter((account) => account.status === 'CONNECTED')
                      .map((account) => account.platform),
                  ).size
                }{' '}
                de 3 conectadas
              </span>
            </div>
            {platforms.map((platform) => (
              <div className="platform-row" key={platform.id}>
                <span className={`platform-icon ${platform.id}`}>
                  {platform.monogram}
                </span>
                <div>
                  <strong>{platform.name}</strong>
                  <p>
                    <WifiOff size={13} />{' '}
                    {data.accounts.some(
                      (account) =>
                        account.platform === platform.id &&
                        account.status === 'CONNECTED',
                    )
                      ? 'Conectado'
                      : 'Não conectado'}
                  </p>
                </div>
                <button
                  aria-label={`Configurar ${platform.name}`}
                  onClick={onOpenSocial}
                >
                  <ChevronRight size={19} />
                </button>
              </div>
            ))}
            <p className="panel-note">
              <Shield size={15} /> Conexões usam APIs oficiais e dependem das
              permissões concedidas por cada plataforma.
            </p>
          </article>
          <article className="panel automation">
            <div className="panel-title">
              <div>
                <p className="eyebrow">SEGURANÇA OPERACIONAL</p>
                <h2>Automações</h2>
              </div>
              <span className={paused ? 'state-dot paused' : 'state-dot'}>
                {paused ? 'PAUSADAS' : 'ATIVAS'}
              </span>
            </div>
            <div className="automation-visual">
              <span className={paused ? 'big-control paused' : 'big-control'}>
                {paused ? <Pause /> : <Check />}
              </span>
              <div>
                <strong>
                  {paused ? 'Tudo em pausa' : 'Sistema protegido e ativo'}
                </strong>
                <p>
                  {paused
                    ? 'Nenhuma ação externa será executada.'
                    : 'O kill switch protege toda ação externa.'}
                </p>
              </div>
            </div>
            <button
              className={paused ? 'resume-button' : 'pause-button'}
              disabled={busy}
              onClick={() => {
                void togglePause();
              }}
            >
              {paused ? (
                <>
                  <Play size={18} /> Retomar automações
                </>
              ) : (
                <>
                  <Pause size={18} /> Pausar automações
                </>
              )}
            </button>
          </article>
          <article className="panel agent">
            <div className="panel-title">
              <div>
                <p className="eyebrow">PROCESSAMENTO LOCAL</p>
                <h2>Local Agent</h2>
              </div>
              <Radio size={20} />
            </div>
            <div className="agent-state">
              <span>
                <Activity />
              </span>
              <div>
                <strong>
                  {data.localAgent
                    ? agentOnline
                      ? 'ONLINE'
                      : 'OFFLINE · heartbeat vencido'
                    : 'NÃO CONFIGURADO'}
                </strong>
                <p>
                  {data.localAgent
                    ? agentDetails(data.localAgent)
                    : 'Conecte um agente para tarefas locais futuras.'}
                </p>
              </div>
            </div>
            <div className="availability">
              <i />
              <span>Cloud Control Plane disponível</span>
            </div>
          </article>
        </section>
      </main>
      <footer>
        <span>GESTOR DE MÍDIA · CÉREBRO EDITORIAL</span>
        <span>
          Modo{' '}
          {data.automation.operation_mode === 'SUPERVISED'
            ? 'supervisionado'
            : 'autônomo'}
        </span>
      </footer>
    </div>
  );
}
